import { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';
import LoginPage from '../Auth/LoginPage.js';
import JourneyManagement from './JourneyManagement';
import UserManagement from './UserManagement';
import PremiumContentManagement from './PremiumContentManagement';
import SeedPremiumContent from './SeedPremiumContent';
import SettingsManagement from './SettingsManagement';
import PaymentsManagement from './PaymentsManagement';
import { 
  Map, 
  Crown, 
  Users, 
  CreditCard, 
  Settings, 
  LogOut, 
  Sprout,
  Loader,
  AlertTriangle,
  Home
} from 'lucide-react';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles } from '../shared/config/theme';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('journeys');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuthAndAdmin();

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
        checkAdminAccess(session.user);
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setIsAdmin(false);
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkAuthAndAdmin = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsAuthenticated(false);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setUser(user);
      setIsAuthenticated(true);
      await checkAdminAccess(user);
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setLoading(false);
    }
  };

  const checkAdminAccess = async (currentUser) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_admin, email')
        .eq('id', currentUser.id)
        .maybeSingle();

      console.log('Admin check - Profile:', profile, 'Error:', error);

      if (error || !profile || !profile.is_admin) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);
      setLoading(false);
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    console.log('Auth success - rechecking admin access');
    setLoading(true);
    checkAuthAndAdmin();
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await supabase.auth.signOut();
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'journeys':
        return <JourneyManagement />;
      case 'users':
        return <UserManagement />;
      case 'premium':
        return <PremiumContentManagement />;
      case 'seed':
        return <SeedPremiumContent />;
      case 'payments':
        return <PaymentsManagement />;
      case 'settings':
        return <SettingsManagement />;
      default:
        return <JourneyManagement />;
    }
  };

  // Navigation items configuration
  const navItems = [
    { id: 'journeys', label: 'Journeys', icon: Map },
    { id: 'premium', label: 'Premium Content', icon: Crown },
    { id: 'seed', label: 'Seed Content', icon: Sprout },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center`}>
        <div className="text-center">
          <Loader className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
          <div className="text-gray-700 text-xl font-semibold">Verifying access...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <LoginPage onAuthSuccess={handleAuthSuccess} />;
  }

  // Authenticated but not admin - show access denied
  if (isAuthenticated && !isAdmin) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center p-6`}>
        <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-8 max-w-md w-full text-center`}>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have admin privileges to access this dashboard.
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <p className="text-gray-700 text-sm">
              Logged in as: <span className="font-semibold text-gray-900">{user?.email}</span>
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={handleLogout}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-6`}
            >
              Logout & Sign In as Admin
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className={`w-full ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} py-3 px-6 flex items-center justify-center gap-2`}
            >
              <Home className="w-4 h-4" />
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated AND admin - show dashboard
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Sidebar Header */}
        <div className={`bg-gradient-to-br ${gradients.button} p-6 text-white`}>
          <h2 className="text-xl font-bold mb-1">Admin Panel</h2>
          <p className="text-orange-100 text-sm mb-3">Manifestation App</p>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2">
            <p className="text-xs text-orange-50 truncate">👤 {user?.email}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === item.id
                    ? `bg-gradient-to-r ${gradients.button} text-white shadow-md`
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setActiveTab(item.id)}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-red-600 hover:bg-red-50 transition-all"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const ComingSoon = ({ title }) => {
  return (
    <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding} text-center`}>
      <div className="py-16">
        <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-peach-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Crown className="w-10 h-10 text-orange-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{title}</h1>
        <p className="text-gray-600 text-lg">This feature is coming soon!</p>
      </div>
    </div>
  );
};

export default AdminDashboard;